import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import LandingNav from '@/components/landing/LandingNav';
import LandingFooter from '@/components/landing/LandingFooter';
import PageMeta from '@/components/landing/PageMeta';
import ApexHeroLogo from '@/components/landing/ApexHeroLogo';
import GovernanceContinuum from '@/components/landing/GovernanceContinuum';

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.2 },
  transition: { duration: 0.6, delay, ease: [0.16, 1, 0.3, 1] },
});

const CLAIMS = [
  ['Your data never leaves your environment',  'Every governance evidence record — every cloud event, every container log — stays in your PostgreSQL, your S3 bucket, encrypted with your KMS key.'],
  ['Apex cannot access your evidence vault',   'No cross-account IAM role. No Apex credentials in your account. No inbound connections from Apex. Not now. Not ever. Not even if you asked us to.'],
  ['Deployable in under 30 minutes',           'One CloudFormation stack. One license key. A post-deploy wizard verifies everything. First governance snapshot or first governance proof chain within hours.'],
];

const CFN = `aws cloudformation create-stack \\
  --stack-name apex-foundation \\
  --template-url https://releases.apex.ai/cfn/foundation-2.1.4.yaml \\
  --parameters \\
    ParameterKey=LicenseKey,ParameterValue=eyJ0eW... \\
    ParameterKey=SSOProviderURL,ParameterValue=https://login.acme.com/saml \\
  --capabilities CAPABILITY_IAM

# Infrastructure ready: ~8 minutes
# First Lens governance snapshot: ~30 minutes
# First Trust proof chain: ~2 hours`;

const HELM = `helm repo add apex https://charts.apex.ai
helm install apex-foundation apex/apex-foundation \\
  --namespace apex \\
  --create-namespace \\
  --set license.key="eyJ0eW..." \\
  --set sso.provider="https://login.acme.com/saml"`;

const TF = `module "apex_foundation" {
  source  = "registry.terraform.io/apex-ai/apex-foundation/aws"
  version = "2.1.4"

  license_key    = var.apex_license_key
  vpc_id         = module.vpc.vpc_id
  subnet_ids     = module.vpc.private_subnet_ids
  kms_key_id     = aws_kms_key.apex.id
}`;

const ADJACENCY = [
  ['ServiceNow',         'GRC workflows',         '+ per-decision evidence'],
  ['Microsoft Purview',  'Compliance reporting',  '+ per-decision evidence'],
  ['OneTrust',           'Policy management',     '+ per-decision evidence'],
  ['Palo Alto Networks', 'Runtime protection',    '+ governance proof alongside defense'],
];

function CodeBlock({ code }) {
  return (
    <pre style={{
      background: '#080D1A', border: '1px solid #1E2A45', borderRadius: 12,
      padding: 20, overflow: 'auto', fontFamily: 'JetBrains Mono, monospace',
      fontSize: 12, color: '#94A3B8', lineHeight: 1.7,
    }}>
      <code style={{ whiteSpace: 'pre' }}>{code}</code>
    </pre>
  );
}

export default function Platform() {
  const [tab, setTab] = useState('cfn');
  const tabs = [
    { id: 'cfn', label: 'CloudFormation', code: CFN },
    { id: 'helm', label: 'Helm', code: HELM },
    { id: 'tf', label: 'Terraform', code: TF },
  ];
  const active = tabs.find(t => t.id === tab);

  return (
    <div style={{ background: '#06070A', minHeight: '100vh', color: '#F1F5F9' }}>
      <PageMeta
        title="Platform — APEX Verity"
        description="Temporal governance reconstruction as a platform. Managed like SaaS, deployed in your environment, your data never leaves. Apex has no credentials for any of it."
        path="/platform"
      />
      <LandingNav />

      {/* Deployment trust banner — page thesis in one line */}
      <div style={{
        background: '#0B0E16',
        borderBottom: '1px solid #14171F',
        padding: '76px 24px 20px',
        textAlign: 'center',
      }} data-testid="platform-deployment-banner">
        <p style={{
          fontFamily: 'JetBrains Mono, monospace',
          fontSize: 12,
          color: '#E2E8F0',
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          margin: 0,
        }}>
          Managed like SaaS · Deployed in your environment · Your data never leaves · Apex has no credentials for any of it
        </p>
      </div>

      {/* Section 1 — Portfolio symmetry */}
      <section style={{ maxWidth: 1100, margin: '0 auto', padding: '80px 24px 64px' }}>
        {/* Hero brand mark — small variant, no tagline (banner above already carries the line) */}
        <motion.div {...fadeUp(0)} style={{ display: 'flex', justifyContent: 'center', marginBottom: 0 }}>
          <ApexHeroLogo width={320} showTagline={false} />
        </motion.div>

        {/* The declaration below the visual */}
        <motion.div {...fadeUp(0.2)} style={{ textAlign: 'center', maxWidth: 640, margin: '40px auto 48px' }}>
          <p style={{ fontSize: 'clamp(20px, 2.2vw, 26px)', color: '#F1F5F9', fontWeight: 500, lineHeight: 1.4, margin: 0, letterSpacing: '-0.01em' }}>
            Governance failed twice. Once in the cloud. Once in AI.
          </p>
          <p style={{ fontSize: 15, color: '#94A3B8', marginTop: 14, lineHeight: 1.75 }}>
            Apex was built to answer both failures with the same philosophy:<br />
            reconstruct what was true, and prove it.
          </p>
        </motion.div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 24, marginTop: 56 }}>
          <motion.div {...fadeUp(0.1)} style={{ border: '1px solid #1E2A45', borderTop: '3px solid #0D9488', borderRadius: 12, padding: 24, background: '#0F1629' }}>
            <h3 style={{ fontSize: 18, fontWeight: 700, margin: 0, color: '#F8FAFC' }}>Lens</h3>
            <p style={{ fontSize: 11, color: '#0D9488', fontFamily: 'JetBrains Mono, monospace', textTransform: 'uppercase', letterSpacing: '0.14em', marginTop: 4 }}>
              Cloud governance truth
            </p>
            <ul style={{ marginTop: 18, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8 }}>
              {['What existed then?', 'What ran in production?', 'Was it approved?', 'What did it access?', 'Governance reconstruction', 'Immutable evidence chain'].map(s => (
                <li key={s} style={{ fontSize: 13, color: '#94A3B8' }}>· {s}</li>
              ))}
            </ul>
          </motion.div>

          <motion.div {...fadeUp(0.2)} style={{ border: '1px solid #1E2A45', borderTop: '3px solid #6366F1', borderRadius: 12, padding: 24, background: '#0F1629' }}>
            <h3 style={{ fontSize: 18, fontWeight: 700, margin: 0, color: '#F8FAFC' }}>Trust</h3>
            <p style={{ fontSize: 11, color: '#6366F1', fontFamily: 'JetBrains Mono, monospace', textTransform: 'uppercase', letterSpacing: '0.14em', marginTop: 4 }}>
              AI governance truth
            </p>
            <ul style={{ marginTop: 18, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8 }}>
              {['What governed decisions then?', 'What controlled the AI?', 'What did it decide?', 'Who reviewed it?', 'Governance reconstruction', 'Immutable proof chain'].map(s => (
                <li key={s} style={{ fontSize: 13, color: '#94A3B8' }}>· {s}</li>
              ))}
            </ul>
          </motion.div>
        </div>

        {/* Inevitability framing — convergence message */}
        <motion.p {...fadeUp(0.05)} style={{
          color: '#64748B',
          fontSize: 13,
          fontStyle: 'italic',
          textAlign: 'center',
          maxWidth: 600,
          margin: '36px auto 0',
          lineHeight: 1.8,
        }}>
          This is not cloud governance and AI governance as separate disciplines.
          This is governance reconstruction as a platform — applied simultaneously
          to the two domains every regulated organization now operates in.
          That convergence is why Apex exists.
        </motion.p>
      </section>

      {/* Section 2 — Deployment Architecture Diagram */}
      <section style={{ background: '#0F1629', borderTop: '1px solid #1E2A45', borderBottom: '1px solid #1E2A45', padding: '80px 24px' }}>
        <div style={{ maxWidth: 1000, margin: '0 auto' }}>
          <motion.h2 {...fadeUp(0)} style={{ fontSize: 11, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.18em', fontFamily: 'JetBrains Mono, monospace', marginBottom: 24, textAlign: 'center' }}>
            Deployment Architecture
          </motion.h2>

          <motion.div {...fadeUp(0.1)}>
            {/* Apex Control Plane */}
            <div style={{ border: '1px solid #1E2A45', borderRadius: 12, padding: 20, background: '#0A0A0F', textAlign: 'center' }}>
              <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 12, color: '#F8FAFC', fontWeight: 700 }}>
                APEX CONTROL PLANE <span style={{ color: '#475569', fontWeight: 400 }}>(Apex-hosted)</span>
              </div>
              <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 10, fontFamily: 'JetBrains Mono, monospace' }}>
                License · Releases · Telemetry · Billing · Support
              </div>
              <div style={{ fontSize: 10, color: '#475569', marginTop: 6, fontFamily: 'JetBrains Mono, monospace' }}>
                Multi-tenant · No customer governed data ever stored here
              </div>
            </div>

            {/* Connector */}
            <div style={{ textAlign: 'center', padding: '10px 0', color: '#475569', fontFamily: 'JetBrains Mono, monospace', fontSize: 11 }}>
              │<br />
              <span style={{ display: 'inline-block', maxWidth: 460 }}>Outbound only from client · No governed data<br />Optional AWS PrivateLink for regulated clients</span><br />
              │<br />▼
            </div>

            {/* Client AWS Account */}
            <div style={{ border: '1px solid #1E2A45', borderRadius: 12, padding: 20, background: '#0A0A0F' }}>
              <div style={{ textAlign: 'center', fontFamily: 'JetBrains Mono, monospace', fontSize: 12, color: '#F8FAFC', fontWeight: 700, marginBottom: 18 }}>
                CLIENT&apos;S AWS ACCOUNT
              </div>

              {/* Foundation */}
              <div style={{ border: '1px solid #1E2A45', borderRadius: 10, padding: 16, background: 'rgba(255,255,255,0.02)' }}>
                <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11, color: '#F8FAFC', fontWeight: 700, textAlign: 'center' }}>
                  APEX FOUNDATION STACK <span style={{ color: '#475569', fontWeight: 400 }}>(shared)</span>
                </div>
                <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 10, color: '#94A3B8', marginTop: 8, textAlign: 'center' }}>
                  License cache · SSO · KMS CMK · Secrets · Health
                </div>
              </div>

              <div style={{ textAlign: 'center', padding: '8px 0', color: '#334155', fontFamily: 'JetBrains Mono, monospace' }}>│ │</div>

              {/* Lens + Trust modules */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 14 }}>
                <div style={{ border: '1px solid rgba(13,148,136,0.5)', borderRadius: 10, padding: 16, background: 'rgba(13,148,136,0.06)' }}>
                  <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11, color: '#0D9488', fontWeight: 700 }}>APEX LENS MODULE</div>
                  <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 10, color: '#475569' }}>(Serverless)</div>
                  <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 10, color: '#94A3B8', marginTop: 8, lineHeight: 1.7 }}>
                    S3 · Athena · DynamoDB<br />Lambda · EventBridge<br />
                    <span style={{ color: '#F8FAFC' }}>All cloud evidence stays here</span>
                  </div>
                </div>
                <div style={{ border: '1px solid rgba(99,102,241,0.5)', borderRadius: 10, padding: 16, background: 'rgba(99,102,241,0.06)' }}>
                  <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11, color: '#6366F1', fontWeight: 700 }}>APEX TRUST MODULE</div>
                  <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 10, color: '#475569' }}>(Containerized)</div>
                  <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 10, color: '#94A3B8', marginTop: 8, lineHeight: 1.7 }}>
                    Aurora PostgreSQL<br />S3 Object Lock<br />ECS/Fargate services<br />
                    <span style={{ color: '#F8FAFC' }}>All AI evidence stays here</span>
                  </div>
                </div>
              </div>

              <div style={{ marginTop: 18, paddingTop: 14, borderTop: '1px solid #1E2A45', textAlign: 'center' }}>
                <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 10, color: '#94A3B8' }}>
                  YOUR KMS KEYS · YOUR S3 BUCKETS · YOUR RDS
                </div>
                <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11, color: '#F59E0B', marginTop: 6, fontWeight: 700, letterSpacing: '0.08em' }}>
                  APEX HAS NO CREDENTIALS FOR ANY OF THIS
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Sovereignty strip — the continuum stops at the customer boundary */}
      <section style={{ maxWidth: 1100, margin: '0 auto', padding: '0 24px' }} data-testid="platform-sovereignty-strip">
        <div style={{ position: 'relative', margin: '40px 0' }}>
          <GovernanceContinuum height={80} opacity={0.2} showLabels={false} />
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            background: '#0A0A0F',
            padding: '4px 16px',
            fontFamily: 'JetBrains Mono, monospace',
            fontSize: 9,
            color: '#10B981',
            textTransform: 'uppercase',
            letterSpacing: '0.2em',
            border: '1px solid rgba(16,185,129,0.2)',
            borderRadius: 4,
            whiteSpace: 'nowrap',
          }}>
            ◆ Your evidence stays here
          </div>
        </div>
      </section>

      {/* Section 3 — Three Claims */}
      <section style={{ maxWidth: 1100, margin: '0 auto', padding: '80px 24px' }}>
        <motion.p {...fadeUp(0)} style={{
          fontSize: 9,
          color: '#475569',
          fontFamily: 'JetBrains Mono, monospace',
          textTransform: 'uppercase',
          letterSpacing: '0.2em',
          marginBottom: 24,
          textAlign: 'center',
        }} data-testid="platform-proofgrade-label">
          Proof-grade deployment architecture
        </motion.p>
        <motion.h2 {...fadeUp(0.04)} style={{ fontSize: 11, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.18em', fontFamily: 'JetBrains Mono, monospace', marginBottom: 24 }}>
          Three Claims
        </motion.h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 14 }}>
          {CLAIMS.map(([title, body], i) => (
            <motion.div key={title} {...fadeUp(0.1 * i)} style={{
              background: '#0F1629', border: '1px solid #1E2A45', borderRadius: 12, padding: 22,
            }}>
              <div style={{ color: '#0D9488', fontWeight: 700, fontSize: 14 }}>◆ {title}</div>
              <p style={{ color: '#94A3B8', fontSize: 12.5, marginTop: 12, lineHeight: 1.7 }}>{body}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Section 4 — Deployment options tabs */}
      <section style={{ maxWidth: 1000, margin: '0 auto', padding: '40px 24px 80px' }}>
        <motion.h2 {...fadeUp(0)} style={{ fontSize: 11, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.18em', fontFamily: 'JetBrains Mono, monospace', marginBottom: 16 }}>
          Deployment Options
        </motion.h2>
        <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid #1E2A45', marginBottom: 16 }}>
          {tabs.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`} style={{
              background: 'transparent', border: 'none', cursor: 'pointer',
              padding: '10px 14px',
              color: tab === t.id ? '#F8FAFC' : '#64748B',
              borderBottom: tab === t.id ? '2px solid #0D9488' : '2px solid transparent',
              fontFamily: 'JetBrains Mono, monospace', fontSize: 12, letterSpacing: '0.04em',
            }}>
              {t.label}
            </button>
          ))}
        </div>
        <CodeBlock code={active.code} />
      </section>

      {/* Section 5 — Strategic Adjacency */}
      <section style={{ maxWidth: 1000, margin: '0 auto', padding: '20px 24px 80px' }}>
        <motion.h2 {...fadeUp(0)} style={{ fontSize: 11, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.18em', fontFamily: 'JetBrains Mono, monospace', marginBottom: 16 }}>
          Strategic Adjacency
        </motion.h2>
        <p style={{ color: '#94A3B8', fontSize: 13, maxWidth: 640, lineHeight: 1.7 }}>
          The governance evidence layer Apex provides is structurally complementary to existing enterprise platforms.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginTop: 24 }}>
          {ADJACENCY.map(([name, role, plus]) => (
            <div key={name} style={{ border: '1px solid #1E2A45', borderRadius: 10, padding: 16, background: '#0F1629' }}>
              <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 12, color: '#F8FAFC', fontWeight: 600 }}>{name}</div>
              <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11, color: '#94A3B8', marginTop: 6 }}>{role}</div>
              <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11, color: '#0D9488', marginTop: 6 }}>{plus}</div>
            </div>
          ))}
        </div>
        <p style={{ color: '#475569', fontSize: 10, marginTop: 18, fontFamily: 'JetBrains Mono, monospace', letterSpacing: '0.06em' }}>
          These are illustrative strategic scenarios, not confirmed relationships.
        </p>
      </section>

      <section style={{ maxWidth: 900, margin: '0 auto', padding: '0 24px 100px', display: 'flex', justifyContent: 'center', gap: 12, flexWrap: 'wrap' }}>
        <Link to="/product/lens" style={{ border: '1px solid #0D9488', color: '#0D9488', padding: '12px 22px', borderRadius: 10, fontSize: 13, fontWeight: 600, textDecoration: 'none' }}>
          See Lens →
        </Link>
        <Link to="/product/trust" style={{ border: '1px solid #6366F1', color: '#818CF8', padding: '12px 22px', borderRadius: 10, fontSize: 13, fontWeight: 600, textDecoration: 'none' }}>
          See Trust →
        </Link>
        <Link to="/contact" style={{ background: '#0D9488', color: 'white', padding: '12px 22px', borderRadius: 10, fontSize: 13, fontWeight: 600, textDecoration: 'none' }}>
          Request pilot →
        </Link>
      </section>
      <LandingFooter />
    </div>
  );
}
